// Resolve a single-segment alias prefix (e.g. `u` in `u.email`) against the
// FROM/JOIN aliases declared in the SQL buffer's enclosing statement.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 6 — final criterion (alias-
// qualified column dispatch). The PRD originally called for a per-Monaco
// `SqlResolverService` for scope walking; for Slice 6's column-with-alias path
// we only need to read aliases at the cursor's enclosing statement, so we
// reuse the resolver's stateless `computeAliasBindingsForRange` against a
// freshly-built `ParsedDocument`. This sidesteps the long-lived service while
// staying compatible with the resolver's alias model — slices 7–9 still wire
// the full service when scope walking genuinely needs it.
//
// The resolver is intentionally cheap-on-call: F12 navigation runs once per
// keypress, and the buffer is typically a single Monaco editor's worth of
// SQL (tens of KB at most). Re-parsing per-call is fine; introducing a cache
// would just duplicate the AST cache the resolver service already owns.
//
// Returns `null` when the buffer fails to parse, when the cursor isn't inside
// any statement, or when the alias name doesn't match any binding in scope.

import { buildParsedDocumentFromSource } from '../../sql-grammar/document';
import type { IdentifierFolding, ParseOptions, QualifiedName, SqlEngine } from '../../sql-grammar/model';
import { computeAliasBindingsForRange } from '../../sql-resolver/scope/aliasScope';
import { foldIdentifier } from '../../sql-resolver/scope/identifiers';
import type { AliasBinding, ParsedBuffer } from '../../sql-resolver/types';

export type AliasResolver = (alias: string) => QualifiedName | null;

export type BuildAliasResolverArgs = {
  readonly source: string;
  readonly cursorOffset: number;
  readonly engine: SqlEngine;
  readonly identifierFolding?: IdentifierFolding;
};

/** Build a memoized lookup `(alias) => QualifiedName | null` for the
 *  statement containing `cursorOffset`. The resolver matches on `aliasFolded`
 *  per the resolver's folding rules so engine-specific case-folding behaves
 *  identically to the rest of SR-RO. */
export function buildAliasResolver(args: BuildAliasResolverArgs): AliasResolver {
  const options: ParseOptions = {
    engine: args.engine,
    identifierFolding: args.identifierFolding ?? defaultFoldingForEngine(args.engine),
  };

  let bindings: readonly AliasBinding[] | null = null;
  const compute = (): readonly AliasBinding[] => {
    if (bindings) return bindings;
    bindings = computeBindingsAtCursor(args.source, args.cursorOffset, options);
    return bindings;
  };

  return (alias) => {
    if (!alias) return null;
    const folded = foldIdentifier(alias, false, options);
    const found = compute().find((b) => b.aliasFolded === folded);
    if (!found) return null;
    return found.target;
  };
}

function computeBindingsAtCursor(
  source: string,
  cursorOffset: number,
  options: ParseOptions,
): readonly AliasBinding[] {
  let parsedDocument;
  try {
    parsedDocument = buildParsedDocumentFromSource(source, options);
  } catch {
    return [];
  }
  const stmt = parsedDocument.statements.find(
    (s) => cursorOffset >= s.range.startOffset && cursorOffset <= s.range.endOffset,
  );
  const start = stmt?.range.startOffset ?? 0;
  const end = stmt?.range.endOffset ?? source.length;

  // computeAliasBindingsForRange only reads parsedDocument off the buffer;
  // the procedural / structure / snapshot fields are untouched. We hand it a
  // partial buffer so we don't pay for a full DocumentStructure build.
  const buffer = { parsedDocument } as unknown as ParsedBuffer;
  try {
    return computeAliasBindingsForRange(buffer, start, end);
  } catch {
    return [];
  }
}

function defaultFoldingForEngine(engine: SqlEngine): IdentifierFolding {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
